import { createContext, useContext, useEffect, useState } from "react";

import { ConfigSchema, type AppConfig } from "./schema";

import type { z } from "zod";

export type { AppConfig };

interface Props {
  children: React.ReactNode;
}

const ConfigContext = createContext<AppConfig | null>(null);

let configPromise: Promise<AppConfig> | null = null;

/** Exposed for test isolation only — resets the module-level fetch cache. */
export function resetConfigCache() {
  configPromise = null;
}

export function fetchConfig(): Promise<AppConfig> {
  if (!configPromise) {
    configPromise = doFetchConfig().catch((e) => {
      configPromise = null;
      throw e;
    });
  }
  return configPromise;
}

async function doFetchConfig(): Promise<AppConfig> {
  const response = await fetch(import.meta.env.VITE_CONFIG_ENDPOINT);

  if (!response.ok) {
    const message = `An error has occurred fetching application configuration: ${response.status}`;
    throw new Error(message);
  }

  const config = await response.json();
  try {
    ConfigSchema.parse(config);
  } catch (err) {
    const messages = (err as z.ZodError).issues
      .map((issue) => `\n  * ${issue.path}: ${issue.message}`)
      .join("");
    console.error("Unable to parse config", config, messages);
  }

  return config;
}

export function ConfigProvider({ children }: Props) {
  const [config, setConfig] = useState<AppConfig | null>(null);

  useEffect(() => {
    const getConfig = async () => {
      const config = await fetchConfig().catch((error: Error) =>
        console.error(error),
      );
      if (config) {
        setConfig(config);
      }
    };

    getConfig();
  }, []);

  return (
    <ConfigContext.Provider value={config}>{children}</ConfigContext.Provider>
  );
}

export const useConfig = () => {
  return useContext(ConfigContext);
};
